import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { ToastrService } from 'ngx-toastr';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { MultiItemSelect } from 'src/app/common/models/multiItemSelect';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { IDisableItemsDto } from 'src/app/common/models/disableItemsDto';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { ItemsService } from 'src/app/services/items.service';
import { catchError, forkJoin, map, Observable, of, tap } from 'rxjs';
import moment from 'moment';
import { LoggingService } from 'src/app/services/logging.service';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { AdminUIHelpers } from 'src/app/common/helpers';
import { StatusService } from 'src/app/services/status.service';
import { ItemGroup } from 'src/app/common/models/itemGroup';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatStepper, MatStep, MatStepLabel, MatStepperPrevious, MatStepperNext } from '@angular/material/stepper';
import { NgClass } from '@angular/common';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';

@Component({
  templateUrl: './item-availability.component.html',
  styleUrls: ['./item-availability.component.scss'],
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    NgClass,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatFormField,
    MatLabel,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatButton,
    MatStepperPrevious,
    MatStepperNext,
    MultiStoreDropdown,
  ],
})
export class ItemAvailabilityComponent implements OnInit {
  public itemsFormGroup: UntypedFormGroup;
  public durationFormGroup: UntypedFormGroup;
  public facilitiesFormGroup: UntypedFormGroup;
  public itemsSelected: MultiItemSelect[] = [];
  public selectAll = false;
  public isUntilFurtherNotice = false;
  public facilities: IMultiStoreSelect[] = [];
  public currentFacilityId: string;
  public reason = '';
  public isClickable = true;
  public isMultiStore = false;
  public disableDuration = '1';
  public authManager: AuthManager;

  // we want to enforce validation on the duration form group when determining if the button is clickable
  public get isDurationClickable(): boolean {
    return (
      this.isClickable
      // button is clickable if the form is valid or untouched
      && (this.durationFormGroup.valid || !this.durationFormGroup.touched)
    );
  }

  constructor(
    public dialogRef: MatDialogRef<ItemAvailabilityComponent>,
    private _facilityManager: FacilityManager,
    private itemService: ItemsService,
    private toastr: ToastrService,
    private authorizationServiceDI: AuthManager,
    private logger: LoggingService,
    @Inject(MAT_DIALOG_DATA) public data: ItemGroup,
    public _formBuilder: UntypedFormBuilder,
    public statusService: StatusService,
  ) {
    this.currentFacilityId = this._facilityManager.getCurrentFacility();

    this.itemsFormGroup = this._formBuilder.group({
      relatedItems: [''],
    });

    this.durationFormGroup = this._formBuilder.group({
      duration: ['1', Validators.required],
      reason: ['', Validators.required],
    });

    this.facilitiesFormGroup = this._formBuilder.group({
      facilities: ['', Validators.required],
    });

    this.durationFormGroup.statusChanges.subscribe((result) =>
      result === 'VALID' ? (this.isClickable = true) : false,
    );

    this.authManager = authorizationServiceDI;
  }

  public ngOnInit(): void {
    this.durationFormGroup.get('duration')?.valueChanges.subscribe((v) => {
      if (v === '0') {
        this.isUntilFurtherNotice = true;
      } else {
        this.isMultiStore = false;
        this.isUntilFurtherNotice = false;
      }
      this.disableDuration = v;
    });
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public multiStoreButton(event: MatRadioChange): void {
    this.isMultiStore = event.value;
  }

  public facilityValidation(): void {
    const facilitiesChecked = this.facilities.filter((f) => f.isSelected).length;
    if (facilitiesChecked) {
      this.facilitiesFormGroup.get('facilities')?.setValue(facilitiesChecked);
      this.isClickable = true;
    } else {
      this.facilitiesFormGroup.get('facilities')?.setValue(null);
      this.isClickable = false;
    }
  }

  public updateAvailability(): void {
    if (!this.durationFormGroup.valid) {
      // this is the 'untouched form' case
      // mark the form as touched, return and don't do anything
      this.markDurationFormTouched();
      return;
    }

    this.isClickable = false;

    const selectedItems: string[] = [];
    const returnItems: ItemGroup = {
      items: this.data.items,
      isAvailable: this.data.isAvailable,
      itemNumber: this.data.itemNumber,
      isExternalItemNumberGroup: this.data.isExternalItemNumberGroup,
      totalSellableQuantity: this.data.items[0].sellableQuantity,
      totalSoldQuantity: this.data.totalSoldQuantity,
      availabilityExceptionFromInventoryManagement: this.data.availabilityExceptionFromInventoryManagement,
      inventorySoldOut: !this.data.isAvailable && this.data.availabilityExceptionFromInventoryManagement,
      availabilityError: this.data.availabilityError,
      isOutOfSync: this.data.isOutOfSync,
    };

    if (selectedItems.length === 0) {
      this.data.items.forEach((item) => {
        selectedItems.push(item.commerceItemNumber);
      });
    }

    const selectedFacilities = AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities);

    if (!this.data.isAvailable) {
      const itemsToDisable: IDisableItemsDto[] = [];

      let enableDate: string;
      if (this.disableDuration === '0') {
        const tempDateMax = new Date(9999, 11, 31, 23, 59, 59, 999);
        enableDate = moment(tempDateMax).startOf('day').format();
      }
      if (this.disableDuration === '1') {
        enableDate = moment().startOf('day').add(1, 'days').format();
      }
      if (this.disableDuration === '2') {
        enableDate = moment().startOf('day').add(2, 'days').format();
      }

      selectedFacilities.forEach((sf) => {
        selectedItems.forEach((si) => {
          itemsToDisable.push({
            commerceItemNumber: si,
            facilityId: sf,
            organizationId: 1,
            enableOn: enableDate,
          });
        });
      });

      this.itemService.setDisabledItems(itemsToDisable).subscribe({
        next: (r) => {
          if (this.data.isExternalItemNumberGroup) {
            this.toastr.success('Commodity Code ' + this.data.itemNumber + ' disabled!');
          } else {
            this.toastr.success('Item ' + this.data.items[0].itemName + ' disabled!');
          }
          this.logger.logEvent('Disabled Items until ' + enableDate.toString() + ': ' + this.data.itemNumber, {
            items: itemsToDisable,
            selectedFacilities: selectedFacilities,
            selectedItems: selectedItems,
            currentFacility: this.currentFacilityId,
          });
          this.dialogRef.close(returnItems);
        },
        error: (e) => {
          this.toastr.error('Error while disabling Item: ' + e);
          this.logger.logException(e);
          this.logger.logTrace('Error while disabling items', { items: itemsToDisable });
          this.isClickable = true;
        },
      });
    } else if (this.data.isAvailable) {
      const enableObservables = this.buildEnabledObservableCollection(selectedItems, selectedFacilities);
      this.statusService.batchProgressSetup(enableObservables.length);
      forkJoin(enableObservables).subscribe({
        next: (r) => {
          const success = this.observableCollectionLogic(r, this.data.isAvailable);

          if (success) {
            const successfulItemsUpdated = r
              .filter((o) => o.facilityId === this.currentFacilityId && o.error === false)
              .map((x) => {
                return x.commerceItemNumber;
              });
            returnItems.items = returnItems.items.filter((ri) =>
              successfulItemsUpdated.includes(ri.commerceItemNumber),
            );
            this.dialogRef.close(returnItems);
          }
        },
        error: (e) => {
          this.toastr.error('An error occurred while updating Item Availability for Facility ');
          this.isClickable = true;
          return;
        },
      });
    }
  }

  public isAvailable(item: ItemGroup): boolean {
    return !item.isAvailable;
  }

  public markDurationFormTouched(): void {
    this.durationFormGroup.markAsTouched();
  }

  private buildEnabledObservableCollection(items: string[], facilities: string[]): Observable<any>[] {
    const saveObservables: Observable<any>[] = [];
    facilities.forEach((f) => {
      items.forEach((i) => {
        saveObservables.push(
          this.itemService
            .setEnabledItems(1, f, i)
            .pipe(
              map((success) => ({ isError: false, error: null, facilityId: f, item: i })),
              catchError((err) => {
                const errObj = of({ isError: true, error: err, facilityId: f, item: i });
                this.logger.logTrace('enable items error', errObj);
                return errObj;
              }),
            )
            .pipe(
              tap(() => {
                this.statusService.incrementCompletedRequests();
              }),
            ),
        );
      });
    });
    return saveObservables;
  }

  private observableCollectionLogic(observableResponses: any[], isAvailable: boolean): boolean {
    const successfulObservables = observableResponses.filter((e) => e.isError === false);
    const erroredObservables = observableResponses.filter((e) => e.isError === true);
    const availabilityStatus = isAvailable ? 'Enabled' : 'Disabled';
    if (erroredObservables.length === 0) {
      this.toastr.success('Item(s) ' + availabilityStatus);
    }
    if (erroredObservables.length > 0 && successfulObservables.length > 0) {
      this.toastr.warning(
        successfulObservables.length
          + ' facility items were '
          + availabilityStatus
          + ', however '
          + erroredObservables.length
          + ' failed to update: '
          + erroredObservables.map((eo) => eo.commerceItemNumber + ' at' + eo.facilityId).join(', '),
        'Not all Facility Items ' + availabilityStatus,
        { timeOut: 10000 },
      );
    }
    if (erroredObservables.length === observableResponses.length) {
      this.toastr.error('An error occurred while updating Facility Items');
      return false;
    }
    return true;
  }
}
